import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import ProjectConfigModal from "../components/ProjectConfigModal";
import LayoutToggle from "../components/projectListing/LayoutToggle";
import ProjectListingClassic from "../components/projectListing/ProjectListingClassic";
import ProjectListingSplit from "../components/projectListing/ProjectListingSplit";
import { useProjectConfig } from "../contexts/ProjectConfigContext";
import { useUiFlag } from "../hooks/useUiFlag";

/**
 * The project listing. Two layouts share this route: the map + list view
 * (default) and the classic tiles/map page, chosen per user through a
 * ui_flag. Everything route-level (the post-create refresh, the config
 * modal) lives here so both layouts behave the same.
 */
export default function ProjectListingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isConfigModalOpen, closeConfigModal, initialTab, contextProjectId: projectId } = useProjectConfig();
  const layout = useUiFlag("projectListingLayout");

  // Arriving from project creation: refetch the list, then drop the marker
  // from the URL (but keep ?folder= so the new project is in view).
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get("refresh") === "true") {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      urlParams.delete("refresh");
      const rest = urlParams.toString();
      navigate(`/projects${rest ? `?${rest}` : ""}`, { replace: true });
    }
  }, [location, navigate, queryClient]);

  // Wait for the user record so the page doesn't flash one layout then the other.
  if (layout.loading) return null;

  const toggle: React.ReactNode = <LayoutToggle />;

  return (
    <>
      {layout.value === "classic" ? (
        <ProjectListingClassic layoutToggle={toggle} />
      ) : (
        <ProjectListingSplit layoutToggle={toggle} />
      )}
      <ProjectConfigModal
        open={isConfigModalOpen}
        onClose={closeConfigModal}
        initialTab={initialTab}
        projectId={projectId}
      />
    </>
  );
}
